import { useState, useEffect } from 'react';
import { Matter, Project, Client, MatterType, MatterTypeField, MatterFieldValue, db } from '../../lib/db';

interface MatterFormProps {
  matter?: Matter;
  projects?: Project[];
  clients?: Client[];
  matterTypes?: MatterType[];
  onSubmit: (data: Partial<Matter>, customFieldValues?: Record<string, any>) => void;
  onCancel: () => void;
}

export function MatterForm({ matter, projects, clients, matterTypes, onSubmit, onCancel }: MatterFormProps) {
  const [title, setTitle] = useState(matter?.title || '');
  const [description, setDescription] = useState(matter?.description || '');
  const [status, setStatus] = useState(matter?.status || 'Active');
  const [projectId, setProjectId] = useState(matter?.project_id || '');
  const [clientId, setClientId] = useState(matter?.client_id || '');
  const [typeId, setTypeId] = useState(matter?.type_id || '');
  const [startDate, setStartDate] = useState(
    matter?.start_date ? new Date(matter.start_date).toISOString().split('T')[0] : ''
  );
  const [deadline, setDeadline] = useState(
    matter?.deadline ? new Date(matter.deadline).toISOString().split('T')[0] : ''
  );
  const [customFields, setCustomFields] = useState<MatterTypeField[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [existingFieldValues, setExistingFieldValues] = useState<MatterFieldValue[]>([]);

  // Load custom fields when matter type changes
  useEffect(() => {
    if (typeId) {
      loadCustomFields();
    } else {
      setCustomFields([]);
      setFieldValues({});
    }
  }, [typeId]);

  // Load existing field values when editing a matter
  useEffect(() => {
    if (matter?.id && customFields.length > 0) {
      loadExistingFieldValues();
    }
  }, [matter?.id, customFields]);

  const loadCustomFields = async () => {
    try {
      const fields = await db.matterTypeFields.findByMatterTypeId(typeId);
      setCustomFields(fields);
      
      // Initialize field values with defaults
      const defaultValues: Record<string, any> = {};
      fields.forEach(field => {
        if (field.field_type === 'boolean') {
          defaultValues[field.id] = false;
        } else if (field.field_type === 'number') {
          defaultValues[field.id] = '';
        } else {
          defaultValues[field.id] = '';
        }
      });
      setFieldValues(defaultValues);
    } catch (error) {
      console.error('Error loading custom fields:', error);
    }
  };

  const loadExistingFieldValues = async () => {
    if (!matter?.id) return;
    
    try {
      const values = await db.matterFieldValues.findByMatterId(matter.id);
      setExistingFieldValues(values);
      
      // Update field values with existing values
      const existingValues: Record<string, any> = { ...fieldValues };
      values.forEach(value => {
        const field = customFields.find(f => f.id === value.field_id);
        if (field) {
          switch (field.field_type) {
            case 'text':
            case 'multiline':
            case 'email':
            case 'url':
              existingValues[value.field_id] = value.value_text || '';
              break;
            case 'number':
              existingValues[value.field_id] = value.value_number || '';
              break;
            case 'date':
              existingValues[value.field_id] = value.value_date || '';
              break;
            case 'boolean':
              existingValues[value.field_id] = value.value_boolean || false;
              break;
            case 'select':
              existingValues[value.field_id] = value.value_json || '';
              break;
          }
        }
      });
      setFieldValues(existingValues);
    } catch (error) {
      console.error('Error loading field values:', error);
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    // Validate required custom fields
    for (const field of customFields) {
      if (field.is_required) {
        const value = fieldValues[field.id];
        if (!value || (typeof value === 'string' && !value.trim())) {
          alert(`Please fill in the required field: ${field.name}`);
          return;
        }
      }
    }

    // Format custom field values for submission
    const formattedFieldValues: Record<string, any> = {};
    customFields.forEach(field => {
      const value = fieldValues[field.id];
      if (value !== '' && value !== null && value !== undefined) {
        formattedFieldValues[field.id] = {
          field,
          value
        };
      }
    });

    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      status,
      project_id: projectId || undefined,
      client_id: clientId || undefined,
      type_id: typeId || undefined,
      start_date: startDate || undefined,
      deadline: deadline || undefined,
    }, formattedFieldValues);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          Title
        </label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          placeholder="Enter matter title"
          required
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          placeholder="Enter matter description (optional)"
        />
      </div>

      {projects && projects.length > 0 && (
        <div>
          <label htmlFor="project_id" className="block text-sm font-medium text-gray-700">
            Project (Optional)
          </label>
          <select
            id="project_id"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">No project selected</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {clients && clients.length > 0 && (
        <div>
          <label htmlFor="client" className="block text-sm font-medium text-gray-700">
            Client (Optional)
          </label>
          <select
            id="client"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">No client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {matterTypes && matterTypes.length > 0 && (
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700">
            Type (Optional)
          </label>
          <select
            id="type"
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">No type selected</option>
            {matterTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
          Status
        </label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        >
          <option value="Active">Active</option>
          <option value="Completed">Completed</option>
          <option value="Archived">Archived</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
            Start Date (Optional)
          </label>
          <input
            type="date"
            id="start_date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
            Deadline (Optional)
          </label>
          <input
            type="date"
            id="deadline"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Custom Fields */}
      {customFields.length > 0 && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">Custom Fields</h3>
          {customFields.map((field) => (
            <div key={field.id}>
              <label htmlFor={field.id} className="block text-sm font-medium text-gray-700">
                {field.name}
                {field.is_required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.description && (
                <p className="mt-1 text-xs text-gray-500">{field.description}</p>
              )}
              
              {field.field_type === 'text' && (
                <input
                  type="text"
                  id={field.id}
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  required={field.is_required}
                />
              )}
              
              {field.field_type === 'multiline' && (
                <textarea
                  id={field.id}
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  required={field.is_required}
                />
              )}
              
              {field.field_type === 'number' && (
                <input
                  type="number"
                  id={field.id}
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  required={field.is_required}
                />
              )}
              
              {field.field_type === 'date' && (
                <input
                  type="date"
                  id={field.id}
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  required={field.is_required}
                />
              )}
              
              {field.field_type === 'boolean' && (
                <div className="mt-1">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      id={field.id}
                      checked={fieldValues[field.id] || false}
                      onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Yes</span>
                  </label>
                </div>
              )}
              
              {field.field_type === 'email' && (
                <input
                  type="email"
                  id={field.id}
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  required={field.is_required}
                />
              )}
              
              {field.field_type === 'url' && (
                <input
                  type="url"
                  id={field.id}
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="https://"
                  required={field.is_required}
                />
              )}
              
              {field.field_type === 'select' && field.options && (
                <select
                  id={field.id}
                  value={fieldValues[field.id] || ''}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  required={field.is_required}
                >
                  <option value="">Choose an option</option>
                  {field.options.map((option: string) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {matter ? 'Update' : 'Create'} Matter
        </button>
      </div>
    </form>
  );
}